import { type FormEvent, useEffect, useState } from 'react';

import { COMPANY_EMAIL } from '../constants/companyContact';
import { SITE_GLASS_CARD } from '../constants/siteUi';
import { PhoneField } from '../components/PhoneField';
import { apiUrl } from '../lib/apiUrl';

/** Bundled by Vite into `/assets/*` so the cover survives SPA “everything → index.html” routing (`public/_redirects`, etc.). */
import IRA_BOOK_COVER_SRC from '@bcc-repo-books/from-401k-to-multifamily-millions/cover.png';

const fieldLabel = 'font-display text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-white/90';
const inputCls =
  'mt-1.5 w-full rounded-xl border border-white/12 bg-white px-3 py-2.5 font-body text-sm text-neutral-900 placeholder:text-neutral-400 shadow-inner shadow-black/5 outline-none transition focus:border-[var(--home-miami-blue)] focus:ring-2 focus:ring-[var(--home-miami-blue)]/35';

const radioRow =
  'flex cursor-pointer items-start gap-2.5 rounded-xl border border-white/[0.08] bg-white/[0.04] px-3 py-2.5 transition hover:bg-white/[0.07] has-[:checked]:border-[var(--home-miami-blue)]/55 has-[:checked]:bg-[var(--home-miami-blue)]/[0.08]';

function BookDownloadDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  /** Mirrors API `bookDownloadEmail.status` after successful POST — informs user when mail was not sent. */
  const [bookEmailDelivery, setBookEmailDelivery] = useState<'sent' | 'skipped' | 'failed' | null>(null);

  useEffect(() => {
    if (!open) return;
    setFirst('');
    setLast('');
    setEmail('');
    setPhone('');
    setErr(null);
    setSent(false);
    setPending(false);
    setBookEmailDelivery(null);
  }, [open]);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setErr(null);
    const fn = first.trim();
    const ln = last.trim();
    const em = email.trim();
    const ph = phone.trim();
    if (!fn || !ln || !em || !ph) {
      setErr('Please fill in every field.');
      return;
    }
    setPending(true);
    try {
      const res = await fetch(apiUrl('/api/public/leads'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'book_401k',
          full_name: `${fn} ${ln}`.trim(),
          email: em,
          phone: ph,
          message: 'Book download request — From 401(k) to Multifamily Millions',
          meta: { funnel: 'ira_book_pdf', first_name: fn, last_name: ln },
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        bookDownloadEmail?: { status: 'sent' | 'skipped' | 'failed' };
      };
      if (!res.ok) throw new Error('submit');
      setBookEmailDelivery(data.bookDownloadEmail?.status ?? 'sent');
      setSent(true);
    } catch {
      setErr('Could not submit right now. Please try again or email us directly.');
    } finally {
      setPending(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-download-title"
        className={`max-h-[min(92vh,620px)] w-full max-w-3xl overflow-hidden ${SITE_GLASS_CARD} !rounded-2xl`}
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="flex max-h-[min(92vh,620px)] flex-col overflow-hidden sm:flex-row sm:items-stretch">
          <aside className="flex shrink-0 flex-col self-stretch border-b border-white/10 bg-gradient-to-b from-black/35 to-black/[0.12] px-4 py-5 sm:w-[15rem] md:w-[17rem] sm:border-b-0 sm:border-r sm:border-white/10 sm:px-5 sm:py-6">
            <figure className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3">
              <div className="w-full max-w-[13rem] overflow-hidden rounded-xl border border-white/15 shadow-[0_12px_40px_-16px_rgba(0,0,0,0.75)] ring-1 ring-black/35 sm:max-w-none">
                <img
                  src={IRA_BOOK_COVER_SRC}
                  alt="From 401(k) to Multifamily Millions by Malcolm Breckenridge — book cover"
                  width={460}
                  height={613}
                  className="mx-auto block h-auto w-full object-contain object-center bg-neutral-950/40 max-h-[min(38vh,280px)] sm:max-h-[calc(min(92vh,620px)-6.5rem)]"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <figcaption className="shrink-0 text-center font-display text-[0.52rem] font-bold uppercase leading-snug tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                Complimentary PDF
              </figcaption>
            </figure>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <div className="shrink-0 border-b border-white/10 px-5 py-4">
              <h2 id="book-download-title" className="font-display text-sm font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)]">
                Download your free book
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-[var(--home-muted-on-dark)]">
                Enter your details — we will email you a link to download{' '}
                <span className="text-[var(--home-text-on-dark)]">From 401(k) to Multifamily Millions</span> (PDF).
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {sent ? (
                <div className="px-5 py-8 font-body text-sm leading-relaxed text-[var(--home-text-on-dark)]">
                  {bookEmailDelivery === 'sent' ? (
                    <p className="rounded-xl border border-[var(--home-miami-blue)]/35 bg-[var(--home-miami-blue)]/[0.08] px-4 py-5">
                      Thank you — check your inbox for an email with a <strong>download button</strong>. If you don&apos;t see it within a few minutes, check spam or promotions.
                    </p>
                  ) : bookEmailDelivery === 'skipped' ? (
                    <p className="rounded-xl border border-white/18 bg-white/[0.06] px-4 py-5">
                      Thank you — we saved your details. We couldn&apos;t send the download link by email from our server yet. Please email{' '}
                      <a
                        href={`mailto:${COMPANY_EMAIL}?subject=${encodeURIComponent('Book PDF — From 401(k) to Multifamily Millions')}`}
                        className="font-medium text-[var(--home-miami-blue)] underline decoration-[var(--home-miami-blue)]/50 underline-offset-2 hover:decoration-[var(--home-miami-blue)]"
                      >
                        {COMPANY_EMAIL}
                      </a>{' '}
                      and we&apos;ll send you the PDF link.
                    </p>
                  ) : (
                    <p className="rounded-xl border border-rose-400/35 bg-rose-500/[0.08] px-4 py-5">
                      Thank you — we saved your details, but the confirmation email failed to send. Please try again in a few minutes or email{' '}
                      <a
                        href={`mailto:${COMPANY_EMAIL}?subject=${encodeURIComponent('Book PDF — From 401(k) to Multifamily Millions')}`}
                        className="font-medium text-[var(--home-miami-blue)] underline decoration-[var(--home-miami-blue)]/50 underline-offset-2 hover:decoration-[var(--home-miami-blue)]"
                      >
                        {COMPANY_EMAIL}
                      </a>{' '}
                      for your copy.
                    </p>
                  )}
                  <button
                    type="button"
                    className="mt-6 w-full rounded-full border border-white/18 bg-white/[0.08] py-3 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)] transition hover:bg-white/[0.12]"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form className="space-y-4 px-5 py-4" onSubmit={(e) => void onSubmit(e)}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className={fieldLabel}>
                      First name*
                      <input
                        value={first}
                        onChange={(e) => setFirst(e.target.value)}
                        required
                        autoComplete="given-name"
                        className={inputCls}
                        placeholder="First name"
                      />
                    </label>
                    <label className={fieldLabel}>
                      Last name*
                      <input
                        value={last}
                        onChange={(e) => setLast(e.target.value)}
                        required
                        autoComplete="family-name"
                        className={inputCls}
                        placeholder="Last name"
                      />
                    </label>
                  </div>
                  <label className={fieldLabel}>
                    Email*
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      type="email"
                      autoComplete="email"
                      className={inputCls}
                      placeholder={COMPANY_EMAIL}
                    />
                  </label>
                  <label className={fieldLabel}>
                    Phone*
                    <PhoneField
                      id="book-download-phone"
                      required
                      value={phone}
                      onChange={setPhone}
                      autoComplete="tel"
                      placeholder="(555) 000-0000"
                      className="mt-1.5"
                      selectClassName={`${inputCls} mt-0 min-w-[7.25rem] shrink-0 cursor-pointer py-2.5 font-body text-xs`}
                      inputClassName={`${inputCls} mt-0 min-w-0 flex-1`}
                    />
                  </label>

                  <p className="rounded-xl border border-dashed border-white/15 bg-white/[0.03] px-3 py-3 font-body text-[0.65rem] leading-relaxed text-[var(--home-muted-on-dark)]">
                    By submitting, you agree we may email you about this resource and related offerings. Unsubscribe anytime from the email footer.
                  </p>

                  {err ? <p className="text-sm text-rose-300">{err}</p> : null}

                  <div className="flex flex-wrap gap-2 border-t border-white/10 pt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="flex-1 rounded-full border border-white/18 bg-white/[0.06] py-3 font-display text-[0.6rem] font-bold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.1]"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={pending}
                      className="flex-[2] rounded-full bg-[var(--header-shell-bg)] py-3 font-display text-[0.62rem] font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-black/30 transition hover:brightness-110 disabled:opacity-60"
                    >
                      {pending ? 'Sending…' : 'Send me the link'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Lead layout: education column + self-directed IRA/401(k) interest form (Breckenridge styling). */
export function Ira401k() {
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [phone, setPhone] = useState('');
  const [bookDialogOpen, setBookDialogOpen] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr(null);
    const fd = new FormData(e.currentTarget);
    const first = String(fd.get('first') ?? '').trim();
    const last = String(fd.get('last') ?? '').trim();
    const email = String(fd.get('email') ?? '').trim();
    const phoneVal = phone.trim();
    const sms = String(fd.get('sms') ?? '');
    const accredited = String(fd.get('accredited') ?? '');
    setPending(true);
    try {
      const res = await fetch(apiUrl('/api/public/leads'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'ira_401k',
          full_name: `${first} ${last}`.trim(),
          email,
          phone: phoneVal,
          meta: { sms_opt_in: sms, accredited },
        }),
      });
      if (!res.ok) throw new Error('submit');
      setSent(true);
    } catch {
      setErr('Could not submit right now. Please email us directly.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="relative mx-auto max-w-[90rem] px-4 pb-20 pt-10 md:px-8 lg:px-10">
      <BookDownloadDialog open={bookDialogOpen} onClose={() => setBookDialogOpen(false)} />
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,380px)] lg:gap-14 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,420px)] xl:gap-20">
        <article className="space-y-8 text-[var(--home-text-on-dark)]">
          <div className="space-y-6 rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-6 shadow-[0_28px_70px_-35px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04] backdrop-blur-sm md:space-y-8 md:p-8">
            <header className="space-y-4">
              <p className="font-display text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-[var(--home-muted-on-dark)]">
                Simplify your retirement
              </p>
              <h1 className="font-display text-balance text-2xl font-bold uppercase leading-[1.15] tracking-[0.03em] text-[var(--home-text-on-dark)] md:text-3xl lg:text-[2.05rem]">
                Invest in{' '}
                <span className="text-[var(--home-miami-blue)]">Breckenridge Capital</span> with your self-directed IRA or 401(k)
              </h1>
            </header>

            <ul className="space-y-3 font-body text-[0.95rem] leading-relaxed text-[var(--home-muted-on-dark)] md:text-base">
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--home-miami-blue)] shadow-[0_0_10px_var(--home-miami-blue)]" aria-hidden />
                <span className="text-[var(--home-text-on-dark)]">Investments backed by tangible real estate assets</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--home-miami-blue)] shadow-[0_0_10px_var(--home-miami-blue)]" aria-hidden />
                <span className="text-[var(--home-text-on-dark)]">May not require distributing cash out of your retirement account</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--home-miami-blue)] shadow-[0_0_10px_var(--home-miami-blue)]" aria-hidden />
                <span className="text-[var(--home-text-on-dark)]">
                  Potential to benefit from income and appreciation according to the offering structure
                </span>
              </li>
            </ul>

            <div className="font-body text-sm leading-relaxed text-[var(--home-muted-on-dark)] md:text-[0.95rem]">
              <p>
                If you maintain an IRA or an employer plan from a prior role, your custodian may permit you to self-direct some or
                all of those assets into alternative investments, subject to plan rules and IRS guidelines. Confirm with your current
                custodian whether self-direction is available. If it is, reach out to our team at{' '}
                <a
                  href={`mailto:${COMPANY_EMAIL}`}
                  className="font-medium text-[var(--home-text-on-dark)] underline decoration-[var(--home-miami-blue)]/60 underline-offset-4 hover:text-[var(--home-miami-blue)]"
                >
                  {COMPANY_EMAIL}
                </a>{' '}
                — we can introduce you to institutional custodians accustomed to private real estate allocations.
              </p>
            </div>
          </div>

          <aside className="rounded-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-6 shadow-[0_28px_70px_-35px_rgba(0,0,0,0.55)] ring-1 ring-white/[0.04] backdrop-blur-sm md:p-8">
            <div className="flex flex-col items-stretch gap-8 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
              <div className="min-w-0 flex-1 space-y-3">
                <p className="font-display text-[0.72rem] font-bold uppercase tracking-[0.18em] text-[var(--header-shell-bg)] drop-shadow-sm">
                  Complimentary book
                </p>
                <p className="font-display text-lg font-bold leading-snug text-[var(--home-text-on-dark)] md:text-xl">
                  From 401(k) to Multifamily Millions
                </p>
                <p className="font-body text-[0.9rem] leading-relaxed text-[var(--home-muted-on-dark)] md:text-[0.95rem]">
                  How to use retirement accounts to build passive income through apartment investing
                </p>
                <button
                  type="button"
                  onClick={() => setBookDialogOpen(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--header-shell-bg)] px-5 py-2.5 font-display text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-white shadow-[0_8px_32px_-12px_rgb(244_114_182/0.55)] ring-1 ring-white/25 transition hover:brightness-110"
                >
                  <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden className="opacity-95">
                    <path d="M12 3v12m0 0l4-4m-4 4-4-4M4 21h16" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Download Free Book Now
                </button>
              </div>

              <figure className="mx-auto w-full max-w-[11.5rem] shrink-0 lg:mx-0">
                <div className="overflow-hidden rounded-xl border border-white/15 bg-black/25 shadow-[0_20px_50px_-24px_rgba(0,0,0,0.7)] ring-1 ring-white/10">
                  <img
                    src={IRA_BOOK_COVER_SRC}
                    alt="From 401(k) to Multifamily Millions by Malcolm Breckenridge — book cover"
                    width={460}
                    height={613}
                    className="aspect-[3/4] h-auto w-full object-cover bg-neutral-950/50"
                    loading="lazy"
                  />
                </div>
                <figcaption className="mt-2.5 text-center font-display text-[0.55rem] font-bold uppercase leading-snug tracking-[0.12em] text-[var(--home-muted-on-dark)]">
                  Malcolm Breckenridge — complimentary PDF
                </figcaption>
              </figure>
            </div>
          </aside>
        </article>

        <aside className="lg:pt-2">
          <div className="sticky top-28 rounded-3xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 p-6 shadow-[0_40px_90px_-40px_rgba(0,0,0,0.65)] ring-1 ring-white/[0.04] backdrop-blur-md md:p-8">
            <h2 className="font-display text-xl font-bold uppercase tracking-[0.12em] text-white">Get started</h2>
            <p className="mt-2 font-body text-[0.8125rem] leading-relaxed text-[var(--home-muted-on-dark)]">
              Tell us how to reach you. A member of our investor relations team will follow up.
            </p>

            {sent ? (
              <div className="mt-8 rounded-2xl border border-[var(--home-miami-blue)]/40 bg-[var(--home-miami-blue)]/[0.08] px-4 py-6 font-body text-sm leading-relaxed text-[var(--home-text-on-dark)]">
                Thank you — we received your request and will follow up shortly.
              </div>
            ) : (
              <form className="mt-8 space-y-5" onSubmit={(e) => void onSubmit(e)}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className={fieldLabel}>
                    First*
                    <input name="first" required autoComplete="given-name" className={inputCls} placeholder="First name" />
                  </label>
                  <label className={fieldLabel}>
                    Last*
                    <input name="last" required autoComplete="family-name" className={inputCls} placeholder="Last name" />
                  </label>
                </div>
                <label className={fieldLabel}>
                  Email*
                  <input name="email" required type="email" autoComplete="email" className={inputCls} placeholder={COMPANY_EMAIL} />
                </label>
                <label className={fieldLabel}>
                  Phone*
                  <PhoneField
                    id="ira-phone"
                    required
                    value={phone}
                    onChange={setPhone}
                    autoComplete="tel"
                    placeholder="(555) 000-0000"
                    className="mt-1.5"
                    selectClassName={`${inputCls} mt-0 min-w-[7.25rem] shrink-0 cursor-pointer py-2.5 font-body text-xs`}
                    inputClassName={`${inputCls} mt-0 min-w-0 flex-1`}
                  />
                </label>

                <fieldset className="space-y-3">
                  <legend className={`${fieldLabel} mb-2`}>Opt in for SMS?*</legend>
                  <label className={radioRow}>
                    <input type="radio" name="sms" value="yes" required className="mt-1 accent-[var(--home-miami-blue)]" />
                    <span className="font-body text-[0.8125rem] leading-snug text-[var(--home-text-on-dark)]">
                      Receive information via SMS
                    </span>
                  </label>
                  <label className={radioRow}>
                    <input type="radio" name="sms" value="no" className="mt-1 accent-[var(--home-miami-blue)]" />
                    <span className="font-body text-[0.8125rem] leading-snug text-[var(--home-text-on-dark)]">
                      Do not receive SMS texts
                    </span>
                  </label>
                </fieldset>

                <fieldset className="space-y-3">
                  <legend className={`${fieldLabel} mb-2`}>Are you an accredited investor?*</legend>
                  <p className="mb-2 font-body text-[0.7rem] leading-relaxed text-[var(--home-muted-on-dark)]">
                    Net worth over $1M excluding primary residence, or income over $200k ($300k jointly) in each of the prior two
                    years with a reasonable expectation for the current year.
                  </p>
                  <label className={radioRow}>
                    <input type="radio" name="accredited" value="no" required className="mt-1 accent-[var(--home-miami-blue)]" />
                    <span className="font-body text-[0.8125rem] text-[var(--home-text-on-dark)]">No</span>
                  </label>
                  <label className={radioRow}>
                    <input type="radio" name="accredited" value="yes" className="mt-1 accent-[var(--home-miami-blue)]" />
                    <span className="font-body text-[0.8125rem] text-[var(--home-text-on-dark)]">Yes</span>
                  </label>
                </fieldset>

                <p className="rounded-xl border border-dashed border-white/15 bg-white/[0.03] px-3 py-3 font-body text-[0.65rem] leading-relaxed text-[var(--home-muted-on-dark)]">
                  Production: add reCAPTCHA before high-volume launch.
                </p>

                {err ? <p className="text-sm text-rose-300">{err}</p> : null}

                <button
                  type="submit"
                  disabled={pending}
                  className="w-full rounded-full bg-[var(--header-shell-bg)] py-3.5 font-display text-[0.65rem] font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-black/30 transition hover:brightness-110 disabled:opacity-60"
                >
                  {pending ? 'Sending…' : 'Get information now'}
                </button>

                <p className="text-center font-body text-[0.65rem] text-[var(--home-muted-on-dark)]">
                  By submitting you agree we may email or call regarding offerings. Offerings available only where permitted by law.
                  See disclosures and suitability requirements in formal offering documents.
                </p>
              </form>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
